import React from "react";

import {
  CarBrand,
  CarModel,
  CommonConfigService,
  CommonConfigSwagger,
  Country,
  SwaggerCommonConfigDao,
  VehicleType,
} from "@egis-smartcity/parking";
import CommonConfigContext, { CommonConfigState } from "./CommonConfigContext";
import useAuth from "../../hooks/useAuth";
import useCityConfig from "../../hooks/useCityConfig";

export type CommonConfigProviderProps = React.PropsWithChildren<{}>;

function CommonConfigProvider(props: CommonConfigProviderProps) {
  const { children } = props;
  const { cityConfig } = useCityConfig();
  const { token } = useAuth();
  const [carBrands, setCarBrands] = React.useState<CarBrand[]>();
  const [carModels, setCarModels] = React.useState<CarModel[]>();
  const [countries, setCountries] = React.useState<Country[]>();
  const [vehicleTypes, setVehicleTypes] = React.useState<VehicleType[]>();

  const commonConfigService: CommonConfigService | undefined =
    React.useMemo(() => {
      if (!cityConfig || !token) return;

      const apiKey = `Bearer ${token}`;

      const mobilitySwaggerClient = new CommonConfigSwagger.MobilityApi(
        new CommonConfigSwagger.Configuration({
          //@ts-ignore
          basePath: cityConfig.commonConfig.mobility.url,
          apiKey,
        })
      );
      const countriesSwaggerClient = new CommonConfigSwagger.CountriesApi(
        new CommonConfigSwagger.Configuration({
          //@ts-ignore
          basePath: cityConfig.commonConfig.country.url,
          apiKey,
        })
      );
      const swaggerCommonConfigDao = new SwaggerCommonConfigDao(
        mobilitySwaggerClient,
        countriesSwaggerClient
      );

      return new CommonConfigService(swaggerCommonConfigDao);
    }, [cityConfig, token]);

  React.useEffect(() => {
    let mounted = true;

    if (!commonConfigService) return;

    commonConfigService
      .getCarBrands()
      .then((carBrands) => {
        if (mounted) setCarBrands(carBrands);
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error(error);
      });

    commonConfigService
      .getCarModels()
      .then((carModels) => {
        if (mounted) setCarModels(carModels);
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error(error);
      });

    commonConfigService
      .getCountries()
      .then((countries) => {
        if (mounted) setCountries(countries);
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error(error);
      });

    commonConfigService
      .getVehicleTypes()
      .then((vehicleTypes) => {
        if (mounted) setVehicleTypes(vehicleTypes);
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error(error);
      });

    return () => {
      mounted = false;
    };
  }, [commonConfigService]);

  const commonConfigState: CommonConfigState = React.useMemo(() => {
    return {
      commonConfigService,
      carBrands,
      carModels,
      countries,
      vehicleTypes,
    };
  }, [carBrands, carModels, commonConfigService, countries, vehicleTypes]);

  return (
    <CommonConfigContext.Provider value={commonConfigState}>
      {children}
    </CommonConfigContext.Provider>
  );
}

export default CommonConfigProvider;
